/**
 * Tuff-Trek Roof Tent Finder
 *
 * Two surfaces, one file:
 *   • the finder bar   ([data-tt-bar])     — collects answers, submits to the
 *                                            results page as a GET query string
 *   • the results page ([data-tt-results]) — scores the collection from those
 *                                            query params and renders the cards
 *
 * Entry-mode submission is a plain form GET, so the bar works with JavaScript
 * disabled and the results URL is shareable, bookmarkable and back-button safe.
 *
 * Two rules run through the whole file:
 *
 *   1. Never present a guessed spec as fact. A value that failed plausibility
 *      validation in Liquid arrives with its `*Known` flag false; it renders
 *      as "TBC" and only feeds a neutral fallback into scoring.
 *   2. The DOM owns the option vocabulary. Each <option> carries data-match
 *      with the catalogue tokens it accepts, so adding a vehicle class is a
 *      change to snippets/tt-finder-bar.liquid alone.
 */

(function () {
  'use strict';

  // ── Budget bands (GBP, VAT inclusive) ────────────────────────
  // Set to the live catalogue spread (£1,195–£5,885). The original bands
  // topped out at "£2,500+", which left the first band empty and lumped over
  // half the range into the last one.
  // `min` is inclusive, `max` is EXCLUSIVE, and each band's max is the next
  // band's min. That matters now that budget is a hard filter: with the old
  // 1499 / 2499 maxima, a tent at £2,499.50 sat in the gap between two bands
  // and was excluded from both, visible only under "Any budget".
  const BUDGET_RANGES = {
    'under-1500': { min: 0, max: 1500 },
    '1500-2500':  { min: 1500, max: 2500 },
    '2500-3500':  { min: 2500, max: 3500 },
    '3500-plus':  { min: 3500, max: Infinity },
    'any':        { min: 0, max: Infinity }
  };

  // Keys must match the `choices` validation on the tent.build_profile
  // metafield definition. 'soft-top' and 'hard-shell' are what the catalogue
  // actually uses — before they were listed here every one of those products
  // silently fell back to the "All-round" label.
  const BUILD_PROFILES = {
    'soft-top':    { label: 'Soft Top' },
    'hard-shell':  { label: 'Hard Shell' },
    'expedition':  { label: 'Expedition-grade' },
    'ultralight':  { label: 'Ultralight' },
    'all-round':   { label: 'All-round' }
  };

  // ── Scoring constants ────────────────────────────────────────
  const SLIDER_WEIGHT = 0.5;
  const DEFAULT_ROOF_RATING = 75;
  // Speed and space ratings are on a 1–10 scale (1 = slowest to pitch /
  // tightest, 10 = fastest / roomiest). The catalogue was always populated on
  // this scale, but the code previously accepted only 1–5, so every
  // hard-shell's 8–9 setup rating was discarded as implausible — which is why
  // soft tops used to outrank pop-up hard-shells on "quick setup".
  const RATING_SCALE = 10;
  const FALLBACK_CAPACITY = 2;
  const FALLBACK_RATING   = RATING_SCALE / 2;
  // Vehicle suitability (Dara's matrix, 1 Sep 2026). A score of 10 is a
  // perfect fit and adds nothing; lower scores subtract, so a 1 on a small
  // car is effectively disqualifying without being a hard filter.
  // Full range: (10 - fit) * weight, so 10 -> 0 and 1 -> -63.
  const FIT_WEIGHT = 7;
  const FIT_UNRATED_PENALTY = 10;

  // Own-brand preference. Applied AFTER the safety and capacity gates, so it
  // can order two viable tents but can never lift an unsafe one into view.
  const OWN_BRAND_BOOST_DEFAULT = 8;

  const UNVERIFIED_USAGE_PENALTY   = 5;
  const UNVERIFIED_CAPACITY_PENALTY = 10;
  const REVEAL_MS = 260;

  // ── Exact matching (11 Sep 2026) ─────────────────────────────
  //
  // Every answer the visitor gives is now a filter, not a preference. A tent
  // that fails one is removed from the results; it is never shown with a
  // reduced match percentage beside it. Scoring still runs, but it only
  // decides the ORDER of the tents that already match.
  //
  // Three things deliberately stay out of the gates because they have no
  // right answer to fail:
  //   • the setup-speed vs interior-space slider (a preference on a
  //     continuum, so it ranks rather than filters)
  //   • the own-brand boost (ordering only)
  //   • any spec we have not confirmed yet, which is a gap in our data and
  //     not a fault of the product. Those stay visible with the gap stated.
  //
  // Vehicle suitability ranks, it does not filter (Dara, 21 Sep 2026: "so
  // long as roof load rating is OK" the vehicle type matters little). What
  // actually decides whether a tent can go on a car is its roof rating, and
  // that is already a hard gate. A tent rated well for the chosen vehicle
  // simply sorts above one rated badly.

  // A budget band is a WINDOW. Both edges filter.
  //
  // The band the customer picks is an answer, not a leaning, and it is read
  // literally: "£1,500 to £2,500" returns tents between those two figures and
  // nothing else, in either direction. An open-ended band is read the same
  // way, so "£3,500+" returns £3,500 and up, and "Under £1,500" returns
  // everything below £1,500.
  //
  // Ranking cheaper tents below the band instead of removing them was tried
  // and rejected: it puts £1,195 tents in front of someone who said £1,500 to
  // £2,500, which is the same complaint as showing a £5,885 tent to someone
  // who said under £1,500, just pointing the other way.

  // Why each tent was ruled out, for the "not shown" breakdown. Order is the
  // priority used when a tent fails more than one gate, so it is counted once
  // and the numbers add up to the total. Safety first, price last.
  const EXCLUSION_ORDER = ['roof', 'sleeping', 'trips', 'budget'];
  const EXCLUSION_LABELS = {
    roof:     { one: 'needs a stronger roof',        many: 'need a stronger roof' },
    sleeping: { one: 'sleeps fewer than you need',   many: 'sleep fewer than you need' },
    trips:    { one: 'does not suit those trips',    many: 'do not suit those trips' },
    // "outside your budget" reads as too expensive, but this bucket also
    // holds tents CHEAPER than the band, which is not what anyone means by
    // over budget. Naming the band keeps it true in both directions.
    budget:   { one: 'is outside your price band',  many: 'are outside your price band' }
  };

  // ── How a tent is sold ───────────────────────────────────────
  // Mirrors the tokens returned by snippets/tt-stock-status.liquid. The
  // finder never decides availability itself any more — it only presents
  // what the classifier says, so it cannot contradict the product page.
  // Every route goes to the product page: reserving is a commitment, and the
  // terms live in the Reserve panel there, not on a results card.
  const STATUS = {
    'stock':         { badge: '',              cls: '',          cta: 'View this tent',      buyable: true  },
    'reserve':       { badge: 'Reserve',       cls: 'reserve',   cta: 'Reserve yours now',   buyable: true  },
    'special-order': { badge: 'Special order', cls: 'special',   cta: 'Request this item',   buyable: false },
    'sold-out':      { badge: 'Sold out',      cls: 'unavailable', cta: 'View this tent',    buyable: false }
  };
  const COMING_SOON = { badge: 'Coming soon', cls: 'soon', cta: 'Coming soon', buyable: false };

  // Compare is capped so the table stays readable on a phone in landscape.
  const COMPARE_MAX = 4;

  const VIEW_PARAM = 'view';
  const VIEW_FULL = 'full';

  // Diagnostic view. Append ?ttdebug=1 to a results URL and the page lists
  // every tent in the collection with the figures it was scored on and the
  // exact gate that rejected it. Never shown without the parameter, so a
  // customer cannot reach it, and it exposes nothing beyond catalogue specs
  // that are already on the product pages.
  //
  // This exists because "why is tent X not showing?" is now a question with
  // a precise answer, and chasing it through the admin one metafield at a
  // time is slow and error-prone: a wrong figure in a metafield looks
  // identical to a bug in the scoring from the outside.
  const DEBUG_PARAM = 'ttdebug';

  const TBC = 'TBC';
  const TBC_TITLE = 'Not yet confirmed for this tent. Ask us and we will check before you order.';

  // Query-string contract between the bar and the results page.
  const FIELD_PARAMS = {
    vehicleType:  'vehicle',
    sleepers:     'sleepers',
    roofRating:   'roof',
    budget:       'budget',
    usage:        'trips',
    speedVsSpace: 'priority'
  };
  // Advanced-panel fields: if any arrives non-default the panel opens itself,
  // so a returning visitor can see every answer that shaped their results.
  const ADVANCED_DEFAULTS = { budget: 'any', trips: 'any', priority: '50' };
  // Plain-English names for the questions, used when an answer arriving in
  // the URL is no longer on offer and the visitor has to be told which one.
  const QUESTION_LABELS = {
    vehicleType:  'vehicle',
    sleepers:     'how many are sleeping',
    roofRating:   'roof load',
    budget:       'budget',
    usage:        'trip style',
    speedVsSpace: 'setup priority'
  };

  // ── Token helpers ────────────────────────────────────────────

  /**
   * Normalises a catalogue token for comparison: lower-cased, trimmed, with
   * runs of spaces / hyphens / underscores collapsed to a single hyphen. Lets
   * '3 Season', '3-season' and '3_Season' compare equal, so a stray space in
   * the admin never silently drops a match.
   */
  function normToken(value) {
    return String(value).trim().toLowerCase().replace(/[\s_-]+/g, '-');
  }

  function normList(value) {
    const arr = Array.isArray(value) ? value : String(value || '').split(',');
    return arr.map(normToken).filter(Boolean);
  }

  /** Text metafields hold 'No' / 'false', which are truthy strings. */
  function isTruthyFlag(value) {
    if (typeof value === 'boolean') return value;
    return ['true', 'yes', '1'].indexOf(normToken(value)) !== -1;
  }

  function intersects(a, b) {
    return a.some(x => b.indexOf(x) !== -1);
  }

  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // ── Scoring ──────────────────────────────────────────────────

  /**
   * @param {object} tent  one entry from window.TT_TENTS
   * @param {object} prefs {budget, vehicleMatch[], usageMatch[], sleepers,
   *                        speedVsSpace, roofRating, roofRatingTouched}
   * @returns {{score:number,
   *            notes:Array<{type:string,code?:string,text:string}>,
   *            excluded:Array<{criterion:string,text:string}>}}
   *
   * `excluded` empty means the tent matches every answer given and is shown.
   * Anything in it means the tent is removed from the results, and `score` is
   * then irrelevant. `score` only orders the tents that already match.
   */
  function computeScore(tent, prefs) {
    let score = 100;
    const notes = [];
    const excluded = [];

    // Records a failed answer. The tent will not be rendered.
    //
    // `text` is not rendered: the "not shown" line groups by criterion, so a
    // 30-tent catalogue reads "24 are outside your budget" rather than 24
    // separate sentences. It is kept because it names the exact figure that
    // failed, which is what makes a wrong metafield findable from the
    // console during QA: TTFinder.computeScore(tent, prefs).excluded.
    function fail(criterion, text) {
      excluded.push({ criterion: criterion, text: text });
    }

    const capacity    = tent.capacityKnown ? tent.capacity    : FALLBACK_CAPACITY;
    const speedRating = tent.speedKnown    ? tent.speedRating : FALLBACK_RATING;
    const spaceRating = tent.spaceKnown    ? tent.spaceRating : FALLBACK_RATING;

    // 1. Budget, a hard gate.
    //
    // Changed 11 Sep 2026 on merchant feedback. Budget used to be a 40-point
    // penalty, so a £5,885 tent could still appear under "Under £1,500" with
    // a match percentage next to it, which reads as an endorsement of a price
    // the customer has already ruled out. A band they picked is an answer,
    // not a leaning, so a price outside it now removes the tent.
    const band = BUDGET_RANGES[prefs.budget] || BUDGET_RANGES['any'];
    if (prefs.budget !== 'any') {
      if (tent.price >= band.max) {
        fail('budget', formatPrice(tent.price) + ' is above the price band you picked.');
      } else if (tent.price < band.min) {
        fail('budget', formatPrice(tent.price) + ' is below the price band you picked.');
      }
    }

    // 2. Vehicle fitment, scored from the suitability matrix.
    // Replaces the old yes/no vehicle_types check. A tent is no longer
    // simply right or wrong for a vehicle: a Pro 1.4 is a 10 on everything,
    // a Pro 2.2 is a 1 on a small car, and the gap between them ranks
    // naturally instead of needing special cases.
    if (prefs.fitKey) {
      const rated = tent.fitKnown && tent.fit && typeof tent.fit[prefs.fitKey] === 'number' && tent.fit[prefs.fitKey] > 0;
      if (rated) {
        const fit = Math.max(1, Math.min(10, tent.fit[prefs.fitKey]));
        score -= (10 - fit) * FIT_WEIGHT;
      } else {
        // Unrated is not the same as unsuitable. A small penalty keeps an
        // unconfigured tent below its rated equivalents without burying it.
        score -= FIT_UNRATED_PENALTY;
        notes.push({
          type: 'caution',
          kind: NOTE_GAP,
          text: 'Vehicle suitability for this tent is still to be confirmed. Check with us before ordering.'
        });
      }
    }

    // 3. Roof load safety.
    // Hard penalties only apply once the visitor has given us a real rating.
    if (prefs.roofRatingTouched) {
      const r = prefs.roofRating;
      // The band the visitor picked. Falls back to treating their answer as
      // an exact figure if the option carries no bounds.
      const band = prefs.roofBand || { low: r, high: r };
      // Rounded UP before comparing. The bands are whole numbers (the top
      // of one is 99, the bottom of the next is 100), but these metafields
      // accept decimals, so a 99.5kg requirement was above the top of the
      // 75-99 band and below the bottom of the 100+ band: no band treated it
      // as a clean pass. Rounding up closes the gap on the conservative
      // side, which is the correct side on a roof load figure.
      const need = tent.minRoofKnown ? Math.ceil(tent.minRoofRatingKg) : null;

      if (need !== null && need > band.high) {
        // Genuinely out of reach: even the top of their band cannot carry
        // it. This is the only roof case that should disappear from results.
        fail('roof', `Needs a roof rated to at least ${need}kg, above the range you picked.`);
      } else if (need !== null && need > band.low) {
        // Inside their band. Whether it suits them depends on their exact
        // figure, which we do not have. Showing it with the condition
        // stated is far more useful than hiding it, which is what the old
        // low-edge-only test did to 17 of 30 tents.
        score -= 12;
        notes.push({
          type: 'caution',
          kind: NOTE_PAGE,
          code: 'roof-borderline',
          text: `Suits you if your roof is rated ${need}kg or above. Check your handbook figure before ordering.`
        });
      }

      // The missing-figure caution, checked on its own rather than as the
      // tail of the chain above.
      //
      // As the last "else if" it needed BOTH figures missing, and the two
      // branches ahead of it both required a known minimum roof rating. So
      // the one combination that most needs a warning never got one: a tent
      // with a known weight and no minimum roof rating passed silently, and
      // someone who picked "Under 45 kg" for a 20kg factory roof was shown a
      // 40kg tent as a clean exact match with nothing said about it. This is
      // the single input where guessing high is a safety problem, so an
      // unconfirmed figure has to say so.
      if (!tent.minRoofKnown || !tent.weightKnown) {
        const missing = !tent.minRoofKnown && !tent.weightKnown
          ? 'Weight and minimum roof rating are'
          : (tent.minRoofKnown ? 'Weight is' : 'The minimum roof rating is');
        notes.push({
          type: 'caution',
          kind: NOTE_GAP,
          text: `${missing} still to be confirmed for this tent. Check the figure with us before ordering.`
        });
      }

      // The tent's own weight, checked separately and not as the tail of the
      // chain above. A tent can have a minimum roof rating inside the band
      // (so it lands on the "borderline" branch) and still weigh more than
      // the top of that band: 60kg minimum, 82kg actual, band 55 to 75. As
      // part of the chain that case was never reached, and the tent was
      // shown with a reassuring note instead of being ruled out on weight.
      // At or above, not just above. The roof's dynamic rating has to be
      // HIGHER than the tent's weight, with a margin for bars or a rack
      // (Dara, 21 Sep 2026: a 74kg tent needs at least a 75kg roof), so a
      // tent weighing exactly the top of the band is already too heavy.
      if (tent.weightKnown && Math.ceil(tent.weightKg) >= band.high) {
        fail('roof', `Weighs ${tent.weightKg}kg, which needs a roof rated above the range you picked.`);
      }
    } else if (tent.minRoofKnown && tent.minRoofRatingKg > DEFAULT_ROOF_RATING) {
      notes.push({
        type: 'caution',
        kind: NOTE_PAGE,
        code: 'roof-unanswered',
        text: `Needs a roof rated to at least ${tent.minRoofRatingKg}kg. Check your handbook and pick your roof load above to confirm.`
      });
    }

    // 4. Sleeping capacity, a hard gate once they have told us, and only
    // against a figure we have actually confirmed. `capacity` above falls
    // back to 2 when the metafield is empty, and excluding a tent on that
    // fallback would drop it for a number nobody entered.
    if (prefs.sleepers) {
      if (tent.capacityKnown) {
        if (tent.capacity < prefs.sleepers) {
          fail('sleeping', `Sleeps ${tent.capacity}, fewer than the ${prefs.sleepers} you need.`);
        }
      } else {
        score -= UNVERIFIED_CAPACITY_PENALTY;
        notes.push({
          type: 'caution',
          kind: NOTE_GAP,
          text: 'Sleeping capacity is still to be confirmed for this tent. Ask us before ordering.'
        });
      }
    }

    // 5. Setup speed vs interior space
    const speedPct = (speedRating / RATING_SCALE) * 100;
    const spacePct = (spaceRating / RATING_SCALE) * 100;
    const wantSpace = prefs.speedVsSpace / 100;
    const match = speedPct * (1 - wantSpace) + spacePct * wantSpace;
    score -= (100 - match) * SLIDER_WEIGHT;

    // 6. Trip style. An empty usageMatch means "no preference" and never
    // penalises — no product carries a 'mixed'-style catch-all token.
    if (prefs.usageMatch.length) {
      const tentUsage = normList(tent.usage);
      if (!tent.usageKnown || tentUsage.length === 0) {
        score -= UNVERIFIED_USAGE_PENALTY;
      } else if (!intersects(prefs.usageMatch, tentUsage)) {
        fail('trips', 'Not set up for the kind of trips you picked.');
      }
    }

    // 7. Own-brand preference, applied last and deliberately so.
    //
    // Every safety and capacity penalty above has already been taken off by
    // this point. The boost can reorder two tents that both clear the roof
    // load and sleeping gates; it cannot lift one that failed them into
    // view, because those penalties are far larger than any boost we allow.
    // That distinction is the whole reason this sits at the end.
    if (tent.ownBrand && prefs.ownBrandBoost > 0) {
      score += prefs.ownBrandBoost;
    }

    // Floored at 0 but no longer capped at 100. The cap existed to keep the
    // "% match" badge sane. That badge is gone, and with score reduced to a
    // sort key the cap actively broke ordering: an own-brand tent scoring
    // 106 clamped to 100 and tied with a rival on 100, so the boost that was
    // meant to rank it first did nothing exactly where it mattered most.
    // Neither floored nor capped. Both limits existed to keep a displayed
    // percentage sane, and both flatten the sort: two poorly-fitting but
    // still-valid tents would clamp to 0 and order arbitrarily, which is the
    // same defect the removal of the 100 cap was meant to fix.
    return { score: Math.round(score), notes, excluded };
  }

  // ── Formatting ───────────────────────────────────────────────

  function formatPrice(price) {
    return '£' + Number(price).toLocaleString('en-GB', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }

  function sliderWord(value) {
    if (value <= 10) return 'Fastest setup';
    if (value <= 35) return 'Leaning to quick setup';
    if (value <= 65) return 'Balanced';
    if (value <= 90) return 'Leaning to more space';
    return 'Maximum space';
  }

  // ── Icons ────────────────────────────────────────────────────

  // Notes come in two kinds, and neither is drawn on a product card.
  //
  //   'page'   is about the customer's own roof. It still matters, since it
  //            is what lets a tent near the top of their band be shown at
  //            all, but repeating it in an amber box on card after card made
  //            a normal page read as a broken one. It is summarised once,
  //            in the neutral notice above the results, and the minimum roof
  //            rating it refers to is already printed on every card.
  //
  //   'gap'    tells them we have not finished filling in a metafield. That
  //            is our admin backlog leaking onto the shop. With the vehicle
  //            matrix still empty it landed on every card at once, which
  //            reads as a broken page rather than a careful one, and it asks
  //            the customer to "check with us" about something they cannot
  //            act on.
  //
  // Gap notes are off by default and surface in the Finder Data Coverage
  // report instead, which is where the people who can fix them will see it.
  const NOTE_GAP = 'gap';
  const NOTE_PAGE = 'page';

  // Off unless the merchant turns it on, which is useful while populating
  // metafields on a preview theme and never useful to a shopper.
  const SHOW_GAP_NOTES = (function () {
    try {
      return document.documentElement.dataset.ttFinderShowGaps === 'true';
    } catch (e) {
      return false;
    }
  }());

  const ICON_WARN = '<svg viewBox="0 0 20 20" aria-hidden="true"><path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>';
  const ICON_INFO = '<svg viewBox="0 0 20 20" aria-hidden="true"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-9-3a1 1 0 112 0 1 1 0 01-2 0zm.25 3a.75.75 0 000 1.5h.25v2h-.5a.75.75 0 000 1.5h2.5a.75.75 0 000-1.5h-.5v-2.75a.75.75 0 00-.75-.75h-1z" clip-rule="evenodd"/></svg>';

  const ICON_EMPTY = '<svg viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"/></svg>';

  // ── Card rendering ───────────────────────────────────────────

  function specRow(label, value, known) {
    const cls = known ? 'tt-finder__spec-value' : 'tt-finder__spec-value tt-finder__spec-value--tbc';
    const title = known ? '' : ` title="${escapeHTML(TBC_TITLE)}"`;
    return `
      <div class="tt-finder__spec">
        <span class="tt-finder__spec-label">${escapeHTML(label)}</span>
        <span class="${cls}"${title}>${escapeHTML(value)}</span>
      </div>`;
  }

  function barRow(label, rating, known) {
    // An unknown rating spans the full track as a grey hatch. Drawing it at
    // the scoring fallback (50%) would read as a real middle value — the whole
    // point of TBC is that there is no magnitude to show.
    const pct = known ? (rating / RATING_SCALE) * 100 : 100;
    const fillCls = known ? 'tt-finder__bar-fill' : 'tt-finder__bar-fill tt-finder__bar-fill--tbc';
    const valCls  = known ? 'tt-finder__bar-value' : 'tt-finder__bar-value tt-finder__bar-value--tbc';
    const valText = known ? `${rating}/${RATING_SCALE}` : TBC;
    const title   = known ? '' : ` title="${escapeHTML(TBC_TITLE)}"`;
    return `
      <div class="tt-finder__bar-row">
        <span class="tt-finder__bar-label">${escapeHTML(label)}</span>
        <span class="tt-finder__bar">
          <span class="${fillCls}" style="width: ${pct}%"></span>
        </span>
        <span class="${valCls}"${title}>${valText}</span>
      </div>`;
  }

  /**
   * Resolves the presentation for a tent's sale state.
   * Coming Soon wins over everything: it is an explicit merchant flag saying
   * "not orderable yet", which outranks whatever the stock maths says.
   */
  function statusOf(tent) {
    if (isTruthyFlag(tent.comingSoon)) return COMING_SOON;
    return STATUS[normToken(tent.status)] || STATUS['stock'];
  }

  function vehicleLabel(tent) {
    if (!tent.vehiclesKnown || !tent.vehicleTypes || !tent.vehicleTypes.length) return TBC;
    return tent.vehicleTypes
      .map(v => String(v).trim())
      .filter(Boolean)
      .map(v => v.charAt(0).toUpperCase() + v.slice(1))
      .join(', ');
  }

  function usageLabel(tent) {
    if (!tent.usageKnown || !tent.usage || !tent.usage.length) return TBC;
    // The Liquid emitter sends an array, but the scoring side accepts a comma
    // string too, so this does as well. Without it a single-value usage
    // metafield emitted as a string throws on .map and takes the whole
    // results grid down with it.
    const list = Array.isArray(tent.usage) ? tent.usage : String(tent.usage).split(',');
    return list
      .map(v => String(v).trim())
      .filter(Boolean)
      .map(v => v.charAt(0).toUpperCase() + v.slice(1))
      .join(', ');
  }

  /**
   * The full spec set, in one place, as {label, value, known} rows.
   * Both the advanced card and the compare table read from this, so a spec
   * can never appear on one and not the other.
   */
  function specSet(tent) {
    const profile = BUILD_PROFILES[normToken(tent.buildProfile)] || BUILD_PROFILES['all-round'];
    const rows = [
      { label: 'Build',           value: tent.buildKnown === false ? TBC : profile.label, known: tent.buildKnown !== false },
      { label: 'Sleeps',          value: tent.capacityKnown ? String(tent.capacity) : TBC, known: !!tent.capacityKnown },
      { label: 'Weight',          value: tent.weightKnown ? tent.weightKg + 'kg' : TBC,    known: !!tent.weightKnown },
      { label: 'Min roof rating', value: tent.minRoofKnown ? tent.minRoofRatingKg + 'kg' : TBC, known: !!tent.minRoofKnown },
      { label: 'Setup speed',     value: tent.speedKnown ? tent.speedRating + '/' + RATING_SCALE : TBC, known: !!tent.speedKnown },
      { label: 'Interior space',  value: tent.spaceKnown ? tent.spaceRating + '/' + RATING_SCALE : TBC, known: !!tent.spaceKnown },
      { label: 'Fits',            value: vehicleLabel(tent), known: !!tent.vehiclesKnown },
      { label: 'Best for',        value: usageLabel(tent),   known: !!tent.usageKnown }
    ];
    // Optional: only earn a row where the merchant has populated them, so an
    // unpopulated catalogue does not render a column of TBC.
    if (tent.setupTimeKnown) {
      rows.splice(5, 0, { label: 'Setup time', value: tent.setupTimeMins + ' min', known: true });
    }
    // From Dara's rating table. Display only, not scored.
    if (typeof tent.weatherRating === 'number') {
      rows.push({ label: 'Weather', value: tent.weatherRating + '/' + RATING_SCALE, known: true });
    }
    if (typeof tent.seasonsRating === 'number') {
      rows.push({ label: 'Seasons', value: tent.seasonsRating + '/' + RATING_SCALE, known: true });
    }
    if (typeof tent.thermalLiner === 'number') {
      rows.push({ label: 'Thermal liner', value: tent.thermalLiner + '/' + RATING_SCALE, known: true });
    }
    return rows;
  }

  /**
   * @param {object} tent
   * @param {object} result   scoring result
   * @param {boolean} isTop
   * @param {boolean} full    advanced view — the full spec set plus compare
   */
  function renderCard(tent, result, isTop, full) {
    const state = statusOf(tent);

    const media = tent.image
      ? `<img src="${escapeHTML(tent.image)}" alt="${escapeHTML(tent.name)}" loading="lazy" decoding="async">`
      : `<div class="tt-finder__card-media-empty">${escapeHTML(tent.name)}</div>`;

    // Only notes the customer can act on. Data gaps are filtered out here
    // rather than at the point they are created, so the scoring engine still
    // records them and the diagnostics view can still show them.
    const shownNotes = result.notes.filter(n => n.kind === NOTE_GAP ? SHOW_GAP_NOTES : n.kind !== NOTE_PAGE);
    const notes = shownNotes.length
      ? `<div class="tt-finder__notes">${shownNotes.map(n => `
          <div class="tt-finder__note tt-finder__note--${n.type}">
            ${ICON_WARN}<span>${escapeHTML(n.text)}</span>
          </div>`).join('')}</div>`
      : '';

    const action = state.buyable
      ? `<a class="tt-finder__btn tt-finder__btn--primary" href="${escapeHTML(tent.url)}">${escapeHTML(state.cta)}</a>`
      : `<a class="tt-finder__btn tt-finder__btn--ghost" href="${escapeHTML(tent.url)}">${escapeHTML(state.cta)}</a>`;

    const statusBadge = state.badge
      ? `<span class="tt-finder__badge tt-finder__badge--${state.cls}">${escapeHTML(state.badge)}</span>`
      : '';

    // Basic view answers the lazy visitor's question and stops: how many does
    // it sleep, what kind is it, will it fit my roof, what does it cost.
    const rows = specSet(tent);
    const basicRows = rows.filter(r => ['Sleeps', 'Build', 'Min roof rating'].indexOf(r.label) !== -1);

    const body = full
      ? `
        <div class="tt-finder__bars">
          ${barRow('Setup speed', tent.speedRating, !!tent.speedKnown)}
          ${barRow('Interior space', tent.spaceRating, !!tent.spaceKnown)}
        </div>
        <div class="tt-finder__specs">
          ${rows.filter(r => ['Setup speed', 'Interior space'].indexOf(r.label) === -1)
                .map(r => specRow(r.label, r.value, r.known)).join('')}
        </div>`
      : `
        <div class="tt-finder__specs tt-finder__specs--lean">
          ${basicRows.map(r => specRow(r.label, r.value, r.known)).join('')}
        </div>`;

    const compare = full
      ? `
        <label class="tt-finder__compare-toggle">
          <input type="checkbox" data-tt-compare value="${escapeHTML(String(tent.id))}">
          <span>Compare</span>
        </label>`
      : '';

    return `
      <article class="tt-finder__card${isTop ? ' tt-finder__card--top' : ''}${state.buyable ? '' : ' tt-finder__card--unavailable'}" data-tt-card="${escapeHTML(String(tent.id))}">
        <div class="tt-finder__card-media">
          ${media}
          <div class="tt-finder__badges">
            ${isTop ? '<span class="tt-finder__badge tt-finder__badge--top">Best match</span>' : ''}
            ${statusBadge}
          </div>
        </div>
        <div class="tt-finder__card-body">
          <h2 class="tt-finder__card-name">${escapeHTML(tent.name)}</h2>
          <p class="tt-finder__card-price">${formatPrice(tent.price)} <small>inc. VAT</small></p>
          ${body}
          ${notes}
          <div class="tt-finder__card-actions">${action}${compare}</div>
        </div>
      </article>`;
  }

  // ── Compare ──────────────────────────────────────────────────

  /**
   * Builds the comparison table. Rows are the union of every spec label in
   * play, so a spec one tent has and another does not still gets a row with
   * an explicit TBC rather than being dropped from the comparison entirely.
   */
  function renderCompareTable(tents) {
    const labels = [];
    tents.forEach(t => specSet(t).forEach(r => {
      if (labels.indexOf(r.label) === -1) labels.push(r.label);
    }));

    const head = tents.map(t => `
      <th scope="col">
        <span class="tt-finder__cmp-name">${escapeHTML(t.name)}</span>
        <span class="tt-finder__cmp-price">${formatPrice(t.price)}</span>
      </th>`).join('');

    const body = labels.map(label => {
      const cells = tents.map(t => {
        const row = specSet(t).find(r => r.label === label);
        const known = row ? row.known : false;
        const value = row ? row.value : TBC;
        return `<td class="${known ? '' : 'tt-finder__cmp-tbc'}"${known ? '' : ` title="${escapeHTML(TBC_TITLE)}"`}>${escapeHTML(value)}</td>`;
      }).join('');
      return `<tr><th scope="row">${escapeHTML(label)}</th>${cells}</tr>`;
    }).join('');

    const links = tents.map(t => {
      const state = statusOf(t);
      return `<td><a class="tt-finder__btn tt-finder__btn--${state.buyable ? 'primary' : 'ghost'} tt-finder__btn--sm" href="${escapeHTML(t.url)}">${escapeHTML(state.cta)}</a></td>`;
    }).join('');

    return `
      <div class="tt-finder__cmp-scroll" tabindex="0" role="region" aria-label="Tent comparison table">
        <table class="tt-finder__cmp-table">
          <thead><tr><th scope="col"><span class="tt-finder__sr">Specification</span></th>${head}</tr></thead>
          <tbody>${body}<tr><th scope="row"><span class="tt-finder__sr">Go to product</span></th>${links}</tr></tbody>
        </table>
      </div>`;
  }

  function renderSkeletons(n) {
    let out = '';
    for (let i = 0; i < n; i++) {
      out += `
        <div class="tt-finder__skeleton" aria-hidden="true">
          <div class="tt-finder__skeleton-media"></div>
          <div class="tt-finder__skeleton-body">
            <div class="tt-finder__skeleton-line"></div>
            <div class="tt-finder__skeleton-line"></div>
            <div class="tt-finder__skeleton-line"></div>
          </div>
        </div>`;
    }
    return out;
  }

  /**
   * Groups ruled-out tents by the answer that ruled them out.
   *
   * A tent can fail more than one gate (over budget AND too heavy for the
   * roof). It is counted once, under the highest-priority criterion it
   * failed, so the parts always add up to the whole and the visitor is never
   * told that 9 tents were removed for 14 reasons.
   *
   * @param  {Array} ruledOut  entries of {tent, result}
   * @return {Array<{criterion:string, count:number}>} in EXCLUSION_ORDER
   */
  function exclusionBreakdown(ruledOut) {
    const tally = {};
    ruledOut.forEach(entry => {
      const codes = entry.result.excluded.map(e => e.criterion);
      // First match in EXCLUSION_ORDER wins, so priority is the constant's
      // order rather than the order the gates happen to run in.
      const primary = EXCLUSION_ORDER.filter(c => codes.indexOf(c) !== -1)[0] || codes[0];
      if (!primary) return;
      tally[primary] = (tally[primary] || 0) + 1;
    });
    // EXCLUSION_ORDER first, then anything it does not list. Without the
    // second pass a gate added later without updating EXCLUSION_ORDER would
    // be counted and then silently dropped, and the visitor would read
    // "9 tents are not shown: 6 are outside your budget".
    const extra = Object.keys(tally).filter(c => EXCLUSION_ORDER.indexOf(c) === -1);
    return EXCLUSION_ORDER.concat(extra)
      .filter(c => tally[c])
      .map(c => ({ criterion: c, count: tally[c] }));
  }

  /** ["roof load", "budget"] -> "roof load and budget" */
  function joinWords(list) {
    if (!list.length) return '';
    if (list.length === 1) return list[0];
    return list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1];
  }

  /** "6 are outside your price band and 3 need a stronger roof" */
  function breakdownSentence(breakdown) {
    const parts = breakdown.map(b => {
      const label = EXCLUSION_LABELS[b.criterion];
      if (!label) return b.count + (b.count === 1 ? ' is ruled out' : ' are ruled out');
      return b.count + ' ' + (b.count === 1 ? label.one : label.many);
    });
    if (!parts.length) return '';
    if (parts.length === 1) return parts[0];
    return parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1];
  }

  /**
   * Diagnostic table: every tent, the figures behind its result, and the
   * verdict. Styles are inline on purpose so this needs no stylesheet change
   * and cannot drift from tt-finder.css.
   */
  function renderDiagnostics(scored, prefs, tentTotal) {
    const cell = 'padding:6px 10px;border-bottom:1px solid rgba(0,0,0,.1);vertical-align:top;';
    const head = cell + 'font-weight:700;white-space:nowrap;';

    const fitOf = tent => {
      if (!prefs.fitKey) return 'n/a';
      if (!tent.fitKnown || !tent.fit || typeof tent.fit[prefs.fitKey] !== 'number' || tent.fit[prefs.fitKey] <= 0) return 'TBC';
      return tent.fit[prefs.fitKey] + '/10';
    };
    const num = (known, value, unit) => known ? (value + (unit || '')) : 'TBC';

    const band = BUDGET_RANGES[prefs.budget] || BUDGET_RANGES['any'];
    const roof = prefs.roofRatingTouched
      ? (prefs.roofBand ? prefs.roofBand.low + ' to ' + prefs.roofBand.high + 'kg' : prefs.roofRating + 'kg')
      : 'not answered';

    const answers = [
      ['Budget', prefs.budget + (prefs.budget === 'any' ? '' : ' (' + formatPrice(band.min) + ' to ' + (band.max === Infinity ? 'no limit' : 'under ' + formatPrice(band.max)) + ')')],
      ['Vehicle', prefs.fitKey || 'not answered'],
      ['Sleeping', prefs.sleepers || 'not answered'],
      ['Roof load', roof],
      ['Trips', prefs.usageMatch.length ? prefs.usageMatch.join(', ') : 'no preference'],
      ['Setup vs space', prefs.speedVsSpace + ' (ranking only, never filters)'],
      ['Own-brand boost', prefs.ownBrandBoost + ' (ranking only, never filters)']
    ].map(r => `<tr><td style="${head}">${escapeHTML(r[0])}</td><td style="${cell}">${escapeHTML(String(r[1]))}</td></tr>`).join('');

    // Ruled-out tents first: they are the reason anyone opens this.
    const ordered = scored.slice().sort((a, b) => {
      const ax = a.result.excluded.length ? 0 : 1;
      const bx = b.result.excluded.length ? 0 : 1;
      if (ax !== bx) return ax - bx;
      return b.result.score - a.result.score;
    });

    const rows = ordered.map(s => {
      const t = s.tent;
      const out = s.result.excluded.length;
      const verdict = out
        ? s.result.excluded.map(e => '<strong>' + escapeHTML(e.criterion) + ':</strong> ' + escapeHTML(e.text)).join('<br>')
        : 'Shown, rank score ' + s.result.score;

      // A tent cannot need a roof rated below its own weight, so when these
      // two fields disagree one of them is simply wrong. Worth surfacing
      // here because the weight gate is the stricter of the two: a tent
      // whose weight field is overstated drops out of a roof band its real
      // specification would have passed, and from the outside that is
      // indistinguishable from a scoring bug.
      const contradiction = (t.weightKnown && t.minRoofKnown && t.weightKg > t.minRoofRatingKg)
        ? `<br><span style="color:#b45309;"><strong>Data check:</strong> weight ${escapeHTML(String(t.weightKg))}kg is above its own minimum roof rating ${escapeHTML(String(t.minRoofRatingKg))}kg. One of the two figures is wrong.</span>`
        : '';
      return `<tr style="${out ? 'background:rgba(220,38,38,.06);' : 'background:rgba(22,163,74,.06);'}">
        <td style="${cell}">${escapeHTML(t.name)}</td>
        <td style="${cell}">${escapeHTML(formatPrice(t.price))}</td>
        <td style="${cell}">${escapeHTML(num(t.capacityKnown, t.capacity))}</td>
        <td style="${cell}">${escapeHTML(num(t.weightKnown, t.weightKg, 'kg'))}</td>
        <td style="${cell}">${escapeHTML(num(t.minRoofKnown, t.minRoofRatingKg, 'kg'))}</td>
        <td style="${cell}">${escapeHTML(fitOf(t))}</td>
        <td style="${cell}">${verdict}${contradiction}</td>
      </tr>`;
    }).join('');

    return `
      <details class="tt-finder__debug" open style="margin-top:24px;padding:16px;border:2px dashed #999;border-radius:8px;font-size:13px;line-height:1.45;">
        <summary style="cursor:pointer;font-weight:700;font-size:15px;">
          Finder diagnostics (${ordered.filter(s => !s.result.excluded.length).length} shown, ${ordered.filter(s => s.result.excluded.length).length} ruled out, ${tentTotal} in the collection)
        </summary>
        <p style="margin:12px 0 4px;font-weight:700;">What the page thinks was asked</p>
        <table style="border-collapse:collapse;width:100%;max-width:640px;"><tbody>${answers}</tbody></table>
        <p style="margin:16px 0 4px;font-weight:700;">Every tent, and why</p>
        <div style="overflow-x:auto;">
          <table style="border-collapse:collapse;width:100%;min-width:840px;">
            <thead><tr>
              <th style="${head}text-align:left;">Tent</th>
              <th style="${head}text-align:left;">Price</th>
              <th style="${head}text-align:left;">Sleeps</th>
              <th style="${head}text-align:left;">Weight</th>
              <th style="${head}text-align:left;">Min roof</th>
              <th style="${head}text-align:left;">Fit</th>
              <th style="${head}text-align:left;">Verdict</th>
            </tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
        <p style="margin:12px 0 0;opacity:.75;">TBC means the metafield is empty. An empty field never rules a tent out, so a TBC here is a data gap to fill, not a bug.</p>
      </details>`;
  }

  function emptyState(title, text) {
    return `
      <div class="tt-finder__empty">
        ${ICON_EMPTY}
        <h2 class="tt-finder__empty-title">${escapeHTML(title)}</h2>
        <p class="tt-finder__empty-text">${escapeHTML(text)}</p>
      </div>`;
  }

  // ── Reading answers off a bar ────────────────────────────────

  function fieldEl(form, name) {
    return form.querySelector(`[data-tt-field="${name}"]`);
  }

  /** data-match on the SELECTED option, so the DOM stays the vocabulary. */
  function matchTokens(form, name) {
    const el = fieldEl(form, name);
    if (!el || !el.options) return [];
    const opt = el.options[el.selectedIndex];
    return opt ? normList(opt.dataset.match || '') : [];
  }

  function readPrefs(form) {
    const val = name => {
      const el = fieldEl(form, name);
      return el ? el.value : '';
    };
    const roof = parseInt(val('roofRating'), 10);
    const sleepers = parseInt(val('sleepers'), 10);
    const priority = parseInt(val('speedVsSpace'), 10);

    return {
      budget: val('budget') || 'any',
      vehicleMatch: matchTokens(form, 'vehicleType'),
      ownBrandBoost: (function () {
        const v = parseInt(document.documentElement.dataset.ttOwnBrandBoost, 10);
        return Number.isFinite(v) ? Math.max(0, Math.min(40, v)) : OWN_BRAND_BOOST_DEFAULT;
      }()),
      // data-fit on the selected option, so the vocabulary stays in the DOM.
      fitKey: (function () {
        const el = fieldEl(form, 'vehicleType');
        if (!el || !el.options) return '';
        const opt = el.options[el.selectedIndex];
        return opt && opt.dataset.fit ? opt.dataset.fit : '';
      }()),
      usageMatch: matchTokens(form, 'usage'),
      // 0 means "not told" — the capacity gate is skipped rather than assumed.
      sleepers: Number.isFinite(sleepers) ? sleepers : 0,
      speedVsSpace: Number.isFinite(priority) ? priority : 50,
      roofRating: Number.isFinite(roof) && roof > 0 ? roof : DEFAULT_ROOF_RATING,
      // Safety penalties only fire on a rating the visitor actually chose.
      roofRatingTouched: Number.isFinite(roof) && roof > 0,
      // The band's two edges, read off the selected option. A customer who
      // picks "55 to 75kg" has a roof somewhere in that range, and a tent
      // needing 70kg may well suit them. Scoring against the low edge alone
      // treated every such tent as unsafe and hid it.
      roofBand: (function () {
        const el = fieldEl(form, 'roofRating');
        if (!el || !el.options) return null;
        const opt = el.options[el.selectedIndex];
        if (!opt) return null;
        const lo = parseInt(opt.dataset.roofLow, 10);
        const hi = parseInt(opt.dataset.roofHigh, 10);
        if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null;
        return { low: lo, high: hi };
      }())
    };
  }

  function hasAnyAnswer(form) {
    // Budget and trips sit in the advanced panel and default to 'any', so an
    // untouched panel must not count as an answer. Both are hard filters
    // now, and someone who opens the panel and sets only a budget has told
    // us something real: before this they were sent back to "Tell us about
    // your vehicle" with their budget silently discarded.
    return ['vehicleType', 'sleepers', 'roofRating', 'budget', 'usage'].some(name => {
      const el = fieldEl(form, name);
      return !!(el && el.value && el.value !== 'any');
    });
  }

  function queryFromForm(form) {
    const params = new URLSearchParams();
    Object.keys(FIELD_PARAMS).forEach(field => {
      const el = fieldEl(form, field);
      if (el && el.value !== '') params.set(FIELD_PARAMS[field], el.value);
    });
    return params;
  }

  /**
   * @returns {{advancedTouched:boolean, dropped:string[]}}
   *   `dropped` names the questions whose saved answer no longer exists as
   *   an option, so the page can say so instead of quietly ignoring them.
   */
  function applyQueryToForm(form, params) {
    let advancedTouched = false;
    const dropped = [];
    Object.keys(FIELD_PARAMS).forEach(field => {
      const param = FIELD_PARAMS[field];
      if (!params.has(param)) return;
      const el = fieldEl(form, field);
      if (!el) return;
      const value = params.get(param);

      if (el.tagName === 'SELECT') {
        // A value that is not one of this select's options must not be
        // applied, or the visitor lands on an answer they never gave. But it
        // cannot just be dropped either: the roof bands were re-cut once
        // already, and every bookmarked or shared link from before that
        // carried a roof answer this select no longer offers. Silently
        // ignoring it left roofRatingTouched false, which switches the roof
        // SAFETY gate off entirely, and the visitor was shown tents their
        // roof cannot carry with nothing to say their answer had been lost.
        if (Array.from(el.options).some(o => o.value === value)) {
          el.value = value;
        } else {
          dropped.push(QUESTION_LABELS[field] || field);
        }
      } else {
        el.value = value;
      }

      if (param in ADVANCED_DEFAULTS && value !== ADVANCED_DEFAULTS[param]) {
        advancedTouched = true;
      }
    });
    return { advancedTouched, dropped };
  }

  // ── The finder bar ───────────────────────────────────────────

  function initBar(bar) {
    if (bar.dataset.ttReady === 'true') return;
    bar.dataset.ttReady = 'true';

    const form = bar.querySelector('[data-tt="form"]');
    if (!form) return;

    const toggle = bar.querySelector('[data-tt="advanced-toggle"]');
    const panel = bar.querySelector('[data-tt="advanced"]');
    const sliderOut = bar.querySelector('[data-tt="slider-value"]');
    const slider = fieldEl(form, 'speedVsSpace');

    if (toggle && panel) {
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      });
    }

    const paintSlider = () => {
      if (!slider) return;
      const word = sliderWord(parseInt(slider.value, 10));
      if (sliderOut) sliderOut.textContent = word;
      // The visible readout lives inside an aria-hidden container, so
      // without this a screen reader announced "50" and nothing else: the
      // one control whose number means nothing on its own.
      slider.setAttribute('aria-valuetext', word);
    };
    if (slider) slider.addEventListener('input', paintSlider);
    paintSlider();

    // Setting a control's value in code fires no input/change event, so the
    // results page has to ask for a repaint after restoring answers from the
    // URL — otherwise the readout keeps describing the default.
    bar.ttSyncUI = paintSlider;

    // Let the results page reveal the panel when it restores advanced answers.
    bar.ttOpenAdvanced = () => {
      if (!toggle || !panel) return;
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
    };
  }

  // ── The results page ─────────────────────────────────────────

  function initResults(root) {
    if (root.dataset.ttReady === 'true') return;
    root.dataset.ttReady = 'true';

    const $ = sel => root.querySelector(`[data-tt="${sel}"]`);
    const grid = $('grid');
    const count = $('count');
    const advisory = $('advisory');
    const heading = $('results-heading');
    const bar = root.querySelector('[data-tt-bar]');
    const form = bar ? bar.querySelector('[data-tt="form"]') : null;
    const tents = Array.isArray(window.TT_TENTS) ? window.TT_TENTS : [];

    // Shopify caps an unpaginated collection loop at 50 products. If the
    // collection is bigger, the tail never reaches this script, and every
    // count on the page would otherwise describe the truncated list as if it
    // were the whole range.
    const catalogueTotal = Number(window.TT_TENT_TOTAL) || tents.length;
    const truncated = catalogueTotal > tents.length;
    if (truncated && window.console && console.warn) {
      console.warn(
        '[tt-finder] Only ' + tents.length + ' of ' + catalogueTotal +
        ' products in this collection reached the finder. Shopify caps an ' +
        'unpaginated collection loop at 50, so the rest are not searchable.'
      );
    }

    const viewToggle = $('view-toggle');
    const tray = $('compare-tray');
    const trayCount = $('compare-count');
    const modal = $('compare-modal');
    const modalBody = $('compare-body');

    if (!grid || !form) return;

    // Seed the refine bar from the URL so it always mirrors what produced the
    // results on screen.
    const seedParams = new URLSearchParams(window.location.search);
    const seeded = applyQueryToForm(form, seedParams);
    const advancedTouched = seeded.advancedTouched;
    // Answers the URL carried that this bar can no longer offer. Surfaced on
    // the first render and then cleared, since the visitor only needs
    // telling once.
    let droppedAnswers = seeded.dropped;
    const debugMode = seedParams.get(DEBUG_PARAM) === '1';
    if (advancedTouched && bar.ttOpenAdvanced) bar.ttOpenAdvanced();
    if (bar.ttSyncUI) bar.ttSyncUI();

    // Detail level. Someone who bothered to open the advanced panel has
    // already told us they want the specs, so they land in the full view
    // unless the URL says otherwise. Everyone else gets the lean cards.
    let viewFull = seedParams.has(VIEW_PARAM)
      ? seedParams.get(VIEW_PARAM) === VIEW_FULL
      : advancedTouched;

    // Selected tent ids for comparison. Survives re-scoring and re-rendering,
    // so refining your answers does not silently empty the compare tray.
    const selected = new Set();

    // Host for the diagnostic table, created once and only in debug mode, so
    // the normal page gains no extra node.
    let debugHost = null;
    function paintDebug(scored, prefs) {
      if (!debugMode) return;
      if (!debugHost) {
        debugHost = document.createElement('div');
        debugHost.setAttribute('data-tt', 'debug');
        grid.parentNode.insertBefore(debugHost, grid.nextSibling);
      }
      debugHost.innerHTML = renderDiagnostics(scored, prefs, tents.length);
    }

    function render(opts) {
      const options = opts || {};
      const prefs = readPrefs(form);
      // Every other element is looked up defensively; count was not, so a
      // renamed hook in the section markup threw on the first render and
      // left the page blank instead of degrading.
      const setCount = text => { if (count) count.textContent = text; };
      if (advisory) { advisory.hidden = true; advisory.innerHTML = ''; }

      // render() has several early returns below, and without this the tray
      // keeps whatever count it had before them. It reads only `selected`
      // and `viewFull`, so it is safe to resolve before the results are
      // known, and it is painted again at the end once the tray has been
      // pruned to the tents that actually matched.
      paintTray();

      if (!tents.length) {
        // No results means no comparison. The tray reads `selected`, which
        // survives re-renders on purpose, so without this it stays pinned
        // over the message with an enabled Compare button that opens a table
        // of tents the page has just said do not match.
        selected.clear();
        paintTray();
        setCount('');
        grid.innerHTML = emptyState(
          'No tents to search yet',
          'Choose a roof tent collection in this section’s settings and the finder will pick it up automatically.'
        );
        return;
      }

      if (!hasAnyAnswer(form)) {
        selected.clear();
        paintTray();
        setCount('');
        grid.innerHTML = emptyState(
          'Tell us about your vehicle',
          'Pick your vehicle, how many of you are sleeping and your roof load rating above, and we will rank the range for you.'
        );
        return;
      }

      const scored = tents.map(tent => ({ tent, result: computeScore(tent, prefs) }));

      // Exact matching: a tent is shown only if it fails none of the answers
      // given. Score no longer decides visibility at all, only the order of
      // the tents that already match.
      const shown = scored
        .filter(s => !s.result.excluded.length)
        .sort((a, b) => b.result.score - a.result.score);

      const ruledOut = scored.filter(s => s.result.excluded.length);
      const breakdown = exclusionBreakdown(ruledOut);
      const reasons = breakdownSentence(breakdown);

      // A tent that no longer matches cannot stay ticked for comparison. Its
      // checkbox is gone from the DOM, so there is no way to untick it, and
      // it would keep counting towards COMPARE_MAX: tick four, narrow the
      // budget until three drop out, and every checkbox still on screen is
      // disabled against a cap the visitor cannot see. Under the old soft
      // scoring results rarely dropped out, so keeping the tick was the
      // kinder behaviour. With hard filtering it is the opposite.
      const visibleIds = {};
      shown.forEach(s => { visibleIds[String(s.tent.id)] = true; });
      Array.from(selected).forEach(id => { if (!visibleIds[id]) selected.delete(id); });

      if (!shown.length) {
        // The empty state carries the whole explanation here, so the
        // advisory stays hidden rather than printing the same sentence twice
        // on the same screen.
        if (advisory && droppedAnswers.length) {
          advisory.hidden = false;
          advisory.innerHTML = `${ICON_INFO}<span>We could not apply your saved
            ${escapeHTML(joinWords(droppedAnswers))} ${droppedAnswers.length === 1 ? 'answer' : 'answers'},
            because the options have changed since that link was made. Please set
            ${droppedAnswers.length === 1 ? 'it' : 'them'} again above.</span>`;
        }
        selected.clear();
        setCount('No exact matches');
        grid.innerHTML = emptyState(
          'Nothing matches all of that',
          `Every tent in the range falls outside at least one of your answers: ${reasons}. Widen one of them above, or talk to us and we will find you something that works.`
        );
        paintTray();
        paintDebug(scored, prefs);
        return;
      }

      // Never drop tents, or answers, silently.
      if (advisory) {
        const lines = [];
        if (droppedAnswers.length) {
          lines.push(`We could not apply your saved ${escapeHTML(joinWords(droppedAnswers))}
            ${droppedAnswers.length === 1 ? 'answer' : 'answers'}, because the options have changed since that link was made.
            Please set ${droppedAnswers.length === 1 ? 'it' : 'them'} again above.`);
        }
        if (ruledOut.length) {
          lines.push(`${ruledOut.length} ${ruledOut.length === 1 ? 'tent is' : 'tents are'} not shown:
            ${escapeHTML(reasons)}. Change an answer above to widen the search.`);
        }

        // The roof, said once for the page rather than once per card.
        const hasNote = (entry, code) => entry.result.notes.some(n => n.code === code);
        const nearTop = shown.filter(e => hasNote(e, 'roof-borderline')).length;
        if (prefs.roofRatingTouched && nearTop) {
          lines.push(nearTop === shown.length
            ? 'Check the minimum roof rating on each tent against the dynamic roof load in your vehicle handbook before you order.'
            : `${nearTop === 1 ? '1 of these needs' : nearTop + ' of these need'} a roof rated towards the top of your range.
               Check the minimum roof rating on each tent against the dynamic roof load in your vehicle handbook.`);
        }
        if (!prefs.roofRatingTouched && shown.some(e => hasNote(e, 'roof-unanswered'))) {
          lines.push('Your roof load is not set, so these are not filtered by what your roof can carry. Pick it above before you choose.');
        }
        if (lines.length) {
          advisory.hidden = false;
          // Informational styling. The warning triangle and amber wash made
          // "18 tents are not shown" read as an error on the page rather
          // than as the answer to "where did the rest go".
          advisory.innerHTML = ICON_INFO + lines.map(l => `<span>${l}</span>`).join('');
        }
      }

      // With exact matching, everything listed is a match, so the old
      // "X strong matches of Y" split no longer means anything. The total is
      // carried here as well as in the advisory: the advisory element is
      // optional in the markup, and without the total a visitor would see
      // two cards from a thirty-tent range with nothing saying why.
      setCount(
        (shown.length === 1 ? '1 exact match' : `${shown.length} exact matches`) +
        (ruledOut.length ? ` from ${tents.length} tents` : '') +
        (truncated ? ` (${catalogueTotal - tents.length} more in this collection are not searchable yet)` : '')
      );

      grid.innerHTML = shown
        .map((s, i) => renderCard(s.tent, s.result, i === 0 && shown.length > 1, viewFull))
        .join('');

      // innerHTML wipes the checkboxes, so re-apply the selection. Anything
      // that dropped out of the results has already been pruned from
      // `selected` above, so what is re-applied here is only what is on
      // screen and the COMPARE_MAX cap counts only visible tents.
      if (viewFull) {
        grid.querySelectorAll('[data-tt-compare]').forEach(box => {
          box.checked = selected.has(box.value);
          box.disabled = !box.checked && selected.size >= COMPARE_MAX;
        });
      }
      paintTray();
      paintDebug(scored, prefs);

      if (options.focus && heading) heading.focus({ preventScroll: true });
    }

    // ── Compare ──────────────────────────────────────────────

    function paintTray() {
      if (!tray) return;
      const n = selected.size;
      tray.hidden = !(viewFull && n > 0);
      if (trayCount) {
        trayCount.textContent = n === 1
          ? '1 tent selected, pick another to compare'
          : `${n} tents selected`;
      }
      // Comparing one thing against nothing is not a comparison. The button
      // stays visible so the goal is obvious, but disabled until it works —
      // an enabled button that does nothing is worse than a disabled one.
      const openBtn = $('compare-open');
      if (openBtn) openBtn.disabled = n < 2;
    }

    function selectedTents() {
      // Ordered by the catalogue, not by click order, so the columns do not
      // reshuffle when someone unticks and reticks one.
      return tents.filter(t => selected.has(String(t.id)));
    }

    // Remembered so closing the dialog returns the keyboard where it was,
    // rather than dumping the visitor at the top of the document.
    let lastFocus = null;
    let bodyOverflow = '';

    function openCompare() {
      if (!modal || !modalBody || selected.size < 2) return;
      lastFocus = document.activeElement;
      modalBody.innerHTML = renderCompareTable(selectedTents());
      modal.hidden = false;
      // Remembered rather than assumed empty, so closing does not clobber a
      // scroll lock some other component (a cart drawer) had already set.
      bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const closeBtn = modal.querySelector('[data-tt="compare-close"]');
      if (closeBtn) closeBtn.focus();
    }

    /**
     * Keeps Tab inside the open dialog.
     *
     * The panel is marked role="dialog" aria-modal="true", but nothing behind
     * it is inert, so one Tab past the last link in the comparison table
     * landed on the site header underneath the backdrop. A keyboard user was
     * then navigating an invisible page with no way back except Escape.
     */
    function trapFocus(event) {
      if (event.key !== 'Tab' || !modal || modal.hidden) return;
      const focusable = modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function closeCompare() {
      if (!modal) return;
      modal.hidden = true;
      document.body.style.overflow = bodyOverflow;
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      lastFocus = null;
    }

    if (grid) {
      grid.addEventListener('change', (event) => {
        const box = event.target.closest('[data-tt-compare]');
        if (!box) return;
        if (box.checked) {
          if (selected.size >= COMPARE_MAX) { box.checked = false; return; }
          selected.add(box.value);
        } else {
          selected.delete(box.value);
        }
        grid.querySelectorAll('[data-tt-compare]').forEach(other => {
          other.disabled = !other.checked && selected.size >= COMPARE_MAX;
        });
        paintTray();
      });
    }

    root.addEventListener('click', (event) => {
      const open = event.target.closest('[data-tt="compare-open"]');
      const clear = event.target.closest('[data-tt="compare-clear"]');
      const close = event.target.closest('[data-tt="compare-close"]');
      const backdrop = event.target.closest('[data-tt="compare-backdrop"]');
      if (open) { openCompare(); }
      if (clear) {
        selected.clear();
        grid.querySelectorAll('[data-tt-compare]').forEach(b => { b.checked = false; b.disabled = false; });
        paintTray();
      }
      if (close || backdrop) closeCompare();
    });

    if (!root.ttEscBound) {
      root.ttEscBound = true;
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && modal && !modal.hidden) closeCompare();
        trapFocus(event);
      });
    }

    // ── Detail level toggle ──────────────────────────────────

    if (viewToggle) {
      viewToggle.querySelectorAll('[data-tt-view]').forEach(btn => {
        btn.addEventListener('click', () => {
          const wantFull = btn.dataset.ttView === VIEW_FULL;
          if (wantFull === viewFull) return;
          viewFull = wantFull;
          // Compare only exists in the full view; leaving a hidden selection
          // behind would surprise someone who switched back later.
          if (!viewFull) selected.clear();
          paintTray();
          paintToggle();
          syncUrl();
          render();
        });
      });
    }

    function paintToggle() {
      if (!viewToggle) return;
      viewToggle.querySelectorAll('[data-tt-view]').forEach(btn => {
        const on = (btn.dataset.ttView === VIEW_FULL) === viewFull;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', String(on));
      });
    }

    function syncUrl() {
      const params = queryFromForm(form);
      // Carry the detail level too: a link someone shares should open in the
      // view they were looking at.
      if (viewFull) params.set(VIEW_PARAM, VIEW_FULL);

      // Carry anything we did not put there. queryFromForm rebuilds the
      // query string from the form fields alone, so the first time a visitor
      // touched a dropdown every foreign parameter was wiped: utm_source,
      // gclid, a referral tag, the diagnostic flag. Losing those breaks
      // campaign attribution on exactly the visitors who engaged most.
      const ours = Object.keys(FIELD_PARAMS).map(f => FIELD_PARAMS[f]).concat([VIEW_PARAM]);
      new URLSearchParams(window.location.search).forEach((value, key) => {
        if (ours.indexOf(key) === -1 && !params.has(key)) params.set(key, value);
      });

      const qs = params.toString();
      // The hash is preserved too: it is how an in-page anchor survives.
      window.history.replaceState(null, '',
        window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
    }

    // In refine mode the form never navigates. Re-score in place and rewrite
    // the URL so the page stays shareable.
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      syncUrl();
      if (count) count.textContent = '';
      grid.innerHTML = renderSkeletons(3);
      window.setTimeout(() => render({ focus: true }), REVEAL_MS);
    });

    let debounce;
    form.addEventListener('input', () => {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(() => { syncUrl(); render(); }, 200);
    });

    form.addEventListener('change', () => {
      // A <select> fires input AND change. Without clearing the pending
      // debounce, every dropdown change rendered twice: once here and again
      // 200ms later, and the second render wiped any focus the visitor had
      // moved into the results. The debounce exists for the range slider,
      // which fires input only.
      window.clearTimeout(debounce);
      syncUrl();
      render();
    });

    window.addEventListener('popstate', () => {
      const p = new URLSearchParams(window.location.search);
      droppedAnswers = applyQueryToForm(form, p).dropped;
      viewFull = p.get(VIEW_PARAM) === VIEW_FULL;
      if (bar.ttSyncUI) bar.ttSyncUI();
      paintToggle();
      render();
    });

    paintToggle();
    render();
  }

  // ── Boot ─────────────────────────────────────────────────────

  function initScope(scope) {
    scope.querySelectorAll('[data-tt-bar]').forEach(initBar);
    scope.querySelectorAll('[data-tt-results]').forEach(initResults);
  }

  function initAll() { initScope(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when a merchant adds or edits the section in the theme editor.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target && event.target.querySelectorAll) initScope(event.target);
  });

  window.TTFinder = {
    init: initAll,
    computeScore,
    readPrefs,
    // Exposed so the "not shown" breakdown can be checked from the console
    // during QA without having to click through every combination.
    exclusionBreakdown,
    breakdownSentence,
    normToken,
    normList,
    isTruthyFlag,
    BUDGET_RANGES,
    BUILD_PROFILES,
    RATING_SCALE
  };
})();
